import React, { createContext, useContext, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';

const GET_PLAN_PROGRESS = gql`
  query GetPlanProgress($planTemplateId: ID!) {
    # Get current user's company features for feature flags
    getCurrentUser {
      id
      company {
        id
        features
      }
    }
    # Get the plan template directly (always available)
    planTemplate(id: $planTemplateId) {
      id
      name
      slug
      planType
      description
      assistantName
      estimatedMinutesMin
      estimatedMinutesMax
      planModules {
        id
        title
        description
        estimatedMinutesMin
        estimatedMinutesMax
        planSections {
          id
          title
          description
          planQuestions {
            id
            questionText
            helpText
            widgetType
            placeholder
            options
            validationRules
            sortOrder
            required
            aiSuggestionEnabled
            aiPrompt
            purpose
            category
            outputSection
            gapAction
            metadata
            educationalContents {
              id
              title
              description
              resourceType
              estimatedMinutes
              content_type
              mdBody: md_body
            }
          }
        }
      }
    }
    # Get user's progress (may be null if not started)
    userPlanProgress(planTemplateId: $planTemplateId) {
      id
      status
      completionPercentage
      currentModuleId
      currentSectionId
      currentQuestionId
      userPlanResponses {
        id
        planQuestionId
        answer
        completedAt
        planQuestion {
          id
          questionText
        }
      }
      # Agent chat conversation data
      agentConversationSessionId
      agentChatMessages {
        id
        role
        content
        timestamp
        agentType
      }
    }
    nextPlanQuestion(planTemplateId: $planTemplateId) {
      id
      questionText
      helpText
      widgetType
      placeholder
      options
      validationRules
      sortOrder
      required
      aiSuggestionEnabled
      aiPrompt
      educationalContents {
        id
        title
        description
        content_type
        mdBody: md_body
        resourceType
        estimatedMinutes
      }
    }
  }
`;

const ANSWER_QUESTION = gql`
  mutation AnswerPlanQuestion($planTemplateId: ID!, $planQuestionId: ID!, $answer: JSON!, $aiSuggestionUsed: Boolean) {
    answerPlanQuestion(planTemplateId: $planTemplateId, planQuestionId: $planQuestionId, answer: $answer, aiSuggestionUsed: $aiSuggestionUsed) {
      userPlanProgress {
        id
        completionPercentage
        status
        userPlanResponses {
          id
          planQuestionId
          answer
          completedAt
          planQuestion {
            id
            questionText
          }
        }
      }
      nextQuestion {
        id
        questionText
        helpText
        widgetType
        placeholder
        options
        validationRules
        sortOrder
        required
        aiSuggestionEnabled
        aiPrompt
        educationalContents {
          id
          title
          description
          content_type
          mdBody: md_body
          resourceType
          estimatedMinutes
        }
      }
      errors
    }
  }
`;

const PAUSE_PLAN = gql`
  mutation PausePlan($planTemplateId: ID!, $scheduledResumeAt: ISO8601DateTime) {
    pausePlan(planTemplateId: $planTemplateId, scheduledResumeAt: $scheduledResumeAt) {
      userPlanProgress {
        id
        status
        pausedAt
        scheduledResumeAt
        completionPercentage
      }
      errors
    }
  }
`;

const RESUME_PLAN = gql`
  mutation ResumePlan($planTemplateId: ID!) {
    resumePlan(planTemplateId: $planTemplateId) {
      userPlanProgress {
        id
        status
        completionPercentage
        userPlanResponses {
          id
          planQuestionId
          answer
          completedAt
          planQuestion {
            id
            questionText
          }
        }
      }
      nextQuestion {
        id
        questionText
        helpText
        widgetType
        placeholder
        options
        validationRules
        sortOrder
        required
        aiSuggestionEnabled
        aiPrompt
        educationalContents {
          id
          title
          description
          content_type
          mdBody: md_body
          resourceType
          estimatedMinutes
        }
      }
      errors
    }
  }
`;

const GET_PLAN_QUESTION_SUGGESTION = gql`
  mutation GetPlanQuestionSuggestion($planQuestionId: ID!, $planTemplateId: ID!, $additionalContext: String) {
    getPlanQuestionSuggestion(planQuestionId: $planQuestionId, planTemplateId: $planTemplateId, additionalContext: $additionalContext) {
      suggestion
      reasoning
      confidence
      errors
    }
  }
`;

const PlanBuilderContext = createContext();

export const usePlanBuilder = () => {
  const context = useContext(PlanBuilderContext);
  if (!context) {
    throw new Error('usePlanBuilder must be used within a PlanBuilderProvider');
  }
  return context;
};

export const PlanBuilderProvider = ({ children, planTemplateId, onPlanComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [progress, setProgress] = useState(null);
  const [planTemplate, setPlanTemplate] = useState(null); // Store planTemplate separately so it's never lost
  const [answers, setAnswers] = useState({});
  
  // Animation states for the post-answer sequence
  // actionHistory: persists all completed actions across answers (for chat history display)
  // currentPhases: the phases currently being animated (resets each answer)
  const [actionHistory, setActionHistory] = useState([]); // Array of { id, type: 'editing'|'presenting', timestamp }
  const [currentPhases, setCurrentPhases] = useState([]); // Current animation phases
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [highlightedQuestionId, setHighlightedQuestionId] = useState(null); // Question to highlight in ViewPlanPanel
  
  // Module completion celebration states
  const [completedModules, setCompletedModules] = useState(new Set()); // Track which modules have been celebrated
  const [triggerConfetti, setTriggerConfetti] = useState(false);
  const [celebratingModule, setCelebratingModule] = useState(null); // { id, title, number } of module being celebrated
  
  // Plan-wide completion state
  const [isPlanComplete, setIsPlanComplete] = useState(false);

  // Agent chat state (for plan_builder_agent_chat feature)
  const [agentMessages, setAgentMessages] = useState([]); // Array of { role: 'user'|'assistant', content, suggestion? }
  const [agentConversationId, setAgentConversationId] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [pendingSuggestion, setPendingSuggestion] = useState(null); // { value, questionId }
  const [injectedAnswer, setInjectedAnswer] = useState(null); // Answer to inject into current question widget

  // Inline AI suggestion state (for "Help me with this answer" feature)
  const [inlineSuggestion, setInlineSuggestion] = useState(null); // { suggestion, reasoning, confidence, questionId }
  const [inlineSuggestionLoading, setInlineSuggestionLoading] = useState(false);
  const [inlineSuggestionError, setInlineSuggestionError] = useState(null);
  const [usedAiSuggestion, setUsedAiSuggestion] = useState(false); // Track if current answer used AI suggestion

  // Accept a suggestion from the agent - injects it into the current question widget
  // Can be called with a direct suggestion value (for persisted messages) or uses pendingSuggestion state
  const acceptSuggestion = (suggestionValue = null) => {
    const valueToUse = suggestionValue || pendingSuggestion?.value;
    if (valueToUse) {
      setInjectedAnswer(valueToUse);
      setPendingSuggestion(null);
      // Add a system message to chat
      setAgentMessages(prev => [...prev, {
        id: `system-${Date.now()}`,
        role: 'system',
        content: 'Suggestion applied! You can review and edit it above before submitting.',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  // Clear agent messages (e.g., when moving to next question)
  const clearAgentChat = () => {
    setAgentMessages([]);
    setPendingSuggestion(null);
  };

  // Clear injected answer after it's consumed by the widget
  const clearInjectedAnswer = () => {
    setInjectedAnswer(null);
  };

  // Fetch inline AI suggestion for the current question
  const fetchInlineSuggestion = async (questionId, additionalContext = null) => {
    if (!questionId || !planTemplateId) return;

    setInlineSuggestionLoading(true);
    setInlineSuggestionError(null);
    setInlineSuggestion(null);

    try {
      const result = await getSuggestionMutation({
        variables: {
          planQuestionId: questionId,
          planTemplateId,
          additionalContext
        }
      });

      const { suggestion, reasoning, confidence, errors } = result.data?.getPlanQuestionSuggestion || {};

      if (errors && errors.length > 0) {
        setInlineSuggestionError(errors[0]);
      } else if (suggestion) {
        setInlineSuggestion({
          suggestion,
          reasoning,
          confidence,
          questionId
        });
      } else {
        setInlineSuggestionError('No suggestion available');
      }
    } catch (e) {
      console.error('Error fetching AI suggestion:', e);
      setInlineSuggestionError('Unable to generate suggestion at this time');
    } finally {
      setInlineSuggestionLoading(false);
    }
  };

  // Use the inline suggestion - inject it into the answer field
  const useInlineSuggestion = (suggestion) => {
    if (suggestion) {
      setInjectedAnswer(suggestion);
      setUsedAiSuggestion(true);
      // Don't clear the suggestion card immediately - let user see what was used
    }
  };

  // Dismiss the inline suggestion
  const dismissInlineSuggestion = () => {
    setInlineSuggestion(null);
    setInlineSuggestionError(null);
  };

  // Clear inline suggestion when question changes
  const clearInlineSuggestion = () => {
    setInlineSuggestion(null);
    setInlineSuggestionError(null);
    setInlineSuggestionLoading(false);
    setUsedAiSuggestion(false);
  };

  const { loading, error, data, refetch } = useQuery(GET_PLAN_PROGRESS, {
    variables: { planTemplateId },
    skip: !planTemplateId,
    onCompleted: (data) => {
      if (data) {
        // Store planTemplate from the direct query (always available)
        if (data.planTemplate) {
          setPlanTemplate(data.planTemplate);
        }
        // Set progress, attaching planTemplate for convenience
        setProgress(data.userPlanProgress ? {
          ...data.userPlanProgress,
          planTemplate: data.planTemplate
        } : {
          // Create a minimal progress object if none exists yet
          id: null,
          status: 'not_started',
          completionPercentage: 0,
          userPlanResponses: [],
          planTemplate: data.planTemplate
        });
        setCurrentQuestion(data.nextPlanQuestion);
        // Map existing answers
        const answerMap = {};
        data.userPlanProgress?.userPlanResponses?.forEach(r => {
          answerMap[r.planQuestionId] = r.answer;
        });
        setAnswers(answerMap);

        // Check if plan is already complete (e.g., returning to a completed plan)
        if (data.userPlanProgress?.status === 'completed') {
          setIsPlanComplete(true);
        }

        // Load persisted agent conversation if available
        if (data.userPlanProgress?.agentConversationSessionId) {
          setAgentConversationId(data.userPlanProgress.agentConversationSessionId);
        }

        // Load persisted agent chat messages if available
        if (data.userPlanProgress?.agentChatMessages?.length > 0) {
          const loadedMessages = data.userPlanProgress.agentChatMessages.map(msg => {
            // Parse out suggestion tags from persisted messages (same logic as live messages)
            const suggestionMatch = msg.content?.match(/\[SUGGESTED_ANSWER\](.*?)\[\/SUGGESTED_ANSWER\]/ms);
            const suggestion = suggestionMatch ? suggestionMatch[1].trim() : null;
            const cleanContent = msg.content?.replace(/\[SUGGESTED_ANSWER\].*?\[\/SUGGESTED_ANSWER\]/ms, '').trim();

            return {
              id: msg.id,
              role: msg.role === 'agent' ? 'assistant' : msg.role,
              content: cleanContent || msg.content,
              suggestion: suggestion,
              timestamp: msg.timestamp,
              isAgentChat: true
            };
          });
          setAgentMessages(loadedMessages);
        }
      }
    }
  });

  const [answerQuestionMutation, { loading: submitting }] = useMutation(ANSWER_QUESTION);
  const [pausePlanMutation, { loading: pausing }] = useMutation(PAUSE_PLAN);
  const [resumePlanMutation, { loading: resuming }] = useMutation(RESUME_PLAN);
  const [getSuggestionMutation] = useMutation(GET_PLAN_QUESTION_SUGGESTION);

  // Helper: Get module that contains a given question
  const getModuleByQuestionId = (questionId) => {
    if (!planTemplate?.planModules) return null;
    for (const module of planTemplate.planModules) {
      for (const section of module.planSections || []) {
        if (section.planQuestions?.some(q => q.id === questionId)) {
          return module;
        }
      }
    }
    return null;
  };

  // Helper: Check if a module is complete based on responses
  const isModuleComplete = (moduleId, responsesList) => {
    if (!planTemplate?.planModules) return false;
    const module = planTemplate.planModules.find(m => m.id === moduleId);
    if (!module) return false;

    // Get all question IDs in this module
    const moduleQuestionIds = (module.planSections || []).flatMap(section =>
      (section.planQuestions || []).map(q => q.id)
    );

    // Check if all questions have been answered
    const answeredQuestionIds = new Set(responsesList.map(r => r.planQuestionId));
    return moduleQuestionIds.every(qId => answeredQuestionIds.has(qId));
  };

  // Helper: Get module number (1-indexed)
  const getModuleNumber = (moduleId) => {
    if (!planTemplate?.planModules) return 1;
    const index = planTemplate.planModules.findIndex(m => m.id === moduleId);
    return index + 1;
  };

  // Helper: Check if entire plan is complete
  const isPlanFullyComplete = (responsesList, template) => {
    if (!template?.planModules) return false;
    
    // Get all question IDs in the entire plan
    const allQuestionIds = template.planModules.flatMap(module =>
      (module.planSections || []).flatMap(section =>
        (section.planQuestions || []).map(q => q.id)
      )
    );
    
    // Check if all questions have been answered
    const answeredQuestionIds = new Set(responsesList.map(r => r.planQuestionId));
    return allQuestionIds.length > 0 && allQuestionIds.every(qId => answeredQuestionIds.has(qId));
  };

  // Dismiss module celebration
  const dismissModuleCelebration = () => {
    setCelebratingModule(null);
  };

  const submitAnswer = async (answer, aiSuggestionUsedOverride = null) => {
    if (!currentQuestion) return;

    // Use override if provided, otherwise use tracked state
    const aiSuggestionUsed = aiSuggestionUsedOverride !== null ? aiSuggestionUsedOverride : usedAiSuggestion;

    try {
      const result = await answerQuestionMutation({
        variables: {
          planTemplateId,
          planQuestionId: currentQuestion.id,
          answer,
          aiSuggestionUsed
        }
      });

      if (result.data?.answerPlanQuestion?.errors?.length === 0) {
        const { nextQuestion, userPlanProgress } = result.data.answerPlanQuestion;
        const actionId = `action-${Date.now()}`;
        
        // Start the animation sequence - phases accumulate during animation
        setIsAnimating(true);
        setCurrentPhases([]);
        
        // Phase 1: "Edited Plan" - show and persist
        setCurrentPhases(['editing']);
        setIsUpdatingPlan(true);
        
        await new Promise(resolve => setTimeout(resolve, 600));
        
        // Phase 2: "Presented results" - add to stack (editing persists)
        setCurrentPhases(['editing', 'presenting']);
        setIsUpdatingPlan(false);
        
        // Update progress immediately so ViewPlanPanel shows data
        // Use the separately stored planTemplate to ensure it's never lost
        setProgress(prev => ({
          ...prev,
          ...userPlanProgress,
          planTemplate: planTemplate // Use the separately stored planTemplate
        }));
        setAnswers(prev => ({
          ...prev,
          [currentQuestion.id]: answer
        }));
        
        await new Promise(resolve => setTimeout(resolve, 600));
        
        // Phase 3: "Thinking" - add to stack (editing + presenting persist)
        setCurrentPhases(['editing', 'presenting', 'thinking']);
        
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Phase 4: Move 'editing' and 'presenting' to permanent history, clear current animation
        const answeredQuestionId = currentQuestion.id;
        setActionHistory(prev => [
          ...prev,
          { id: `${actionId}-editing`, type: 'editing', questionId: answeredQuestionId },
          { id: `${actionId}-presenting`, type: 'presenting', questionId: answeredQuestionId }
        ]);
        setCurrentPhases([]);
        setIsAnimating(false);
        
        // Set highlighted question to scroll to and highlight in ViewPlanPanel
        setHighlightedQuestionId(answeredQuestionId);
        
        // Clear highlight after 3 seconds
        setTimeout(() => {
          setHighlightedQuestionId(null);
        }, 3000);
        
        // Check if a module was just completed
        const questionModule = getModuleByQuestionId(answeredQuestionId);
        if (questionModule) {
          // Build updated responses list including the just-answered question
          const updatedResponses = [
            ...(userPlanProgress.userPlanResponses || []),
          ];
          
          if (
            isModuleComplete(questionModule.id, updatedResponses) &&
            !completedModules.has(questionModule.id)
          ) {
            // Mark module as completed (prevent re-celebration)
            setCompletedModules(prev => new Set(prev).add(questionModule.id));
            
            // Trigger celebration after a brief delay
            setTimeout(() => {
              const moduleNumber = getModuleNumber(questionModule.id);
              
              // Trigger confetti
              setTriggerConfetti(true);
              setTimeout(() => setTriggerConfetti(false), 500);
              
              // Set celebrating module to show the widget
              setCelebratingModule({
                id: questionModule.id,
                title: questionModule.title,
                number: moduleNumber
              });
            }, 500);
          }
        }
        
        setCurrentQuestion(nextQuestion);

        // Clear inline suggestion state for new question
        clearInlineSuggestion();

        // Check if the entire plan is now complete (no next question and status is completed)
        if (!nextQuestion && userPlanProgress.status === 'completed') {
          // Plan is fully complete - trigger celebration
          setIsPlanComplete(true);
          
          // Trigger confetti for plan completion
          setTriggerConfetti(true);
          setTimeout(() => setTriggerConfetti(false), 500);
          
          // Signal to parent to show celebration view in canvas
          if (onPlanComplete) {
            setTimeout(() => {
              onPlanComplete();
            }, 1000);
          }
        }
        
        return true;
      }
      return false;
    } catch (e) {
      console.error("Error submitting answer:", e);
      setCurrentPhases([]);
      setIsUpdatingPlan(false);
      setIsAnimating(false);
      throw e;
    }
  };

  // Edit an existing answer (for use from ViewPlanPanel)
  const editAnswer = async (questionId, answer, aiSuggestionUsed = false) => {
    if (!questionId) return false;

    try {
      setIsUpdatingPlan(true);
      
      const result = await answerQuestionMutation({
        variables: {
          planTemplateId,
          planQuestionId: questionId,
          answer,
          aiSuggestionUsed
        }
      });

      if (result.data?.answerPlanQuestion?.errors?.length === 0) {
        const { userPlanProgress } = result.data.answerPlanQuestion;
        
        // Update progress with the new answer
        setProgress(prev => ({
          ...prev,
          ...userPlanProgress,
          planTemplate: planTemplate
        }));
        setAnswers(prev => ({
          ...prev,
          [questionId]: answer
        }));
        
        // Highlight the edited question
        setHighlightedQuestionId(questionId);
        setTimeout(() => {
          setHighlightedQuestionId(null);
        }, 3000);
        
        setIsUpdatingPlan(false);
        return true;
      }
      
      setIsUpdatingPlan(false);
      return false;
    } catch (e) {
      console.error("Error editing answer:", e);
      setIsUpdatingPlan(false);
      setIsAnimating(false);
      return false;
    }
  };

  // Pause the plan with an optional scheduled resume time
  const pausePlan = async (scheduledResumeAt = null) => {
    try {
      const result = await pausePlanMutation({
        variables: {
          planTemplateId,
          scheduledResumeAt
        }
      });

      if (result.data?.pausePlan?.errors?.length === 0) {
        const { userPlanProgress } = result.data.pausePlan;
        setProgress(prev => ({
          ...prev,
          ...userPlanProgress,
          planTemplate
        }));
        return { success: true, progress: userPlanProgress };
      }
      return { success: false, errors: result.data?.pausePlan?.errors };
    } catch (e) {
      console.error("Error pausing plan:", e);
      return { success: false, errors: [e.message] };
    }
  };

  // Resume a paused plan
  const resumePlan = async () => {
    try {
      const result = await resumePlanMutation({
        variables: { planTemplateId }
      });

      if (result.data?.resumePlan?.errors?.length === 0) {
        const { userPlanProgress, nextQuestion } = result.data.resumePlan;
        setProgress(prev => ({
          ...prev,
          ...userPlanProgress,
          planTemplate
        }));
        setCurrentQuestion(nextQuestion);
        // Rebuild answers map
        const answerMap = {};
        userPlanProgress?.userPlanResponses?.forEach(r => {
          answerMap[r.planQuestionId] = r.answer;
        });
        setAnswers(answerMap);
        return { success: true, progress: userPlanProgress, nextQuestion };
      }
      return { success: false, errors: result.data?.resumePlan?.errors };
    } catch (e) {
      console.error("Error resuming plan:", e);
      return { success: false, errors: [e.message] };
    }
  };

  // Ensure progress always has planTemplate attached
  const progressWithTemplate = progress ? {
    ...progress,
    planTemplate: planTemplate || progress.planTemplate
  } : null;

  const value = {
    loading,
    error,
    submitting,
    pausing,
    resuming,
    currentQuestion,
    progress: progressWithTemplate,
    planTemplate,
    planTemplateId,
    answers,
    submitAnswer,
    editAnswer,
    pausePlan,
    resumePlan,
    refetch,
    actionHistory,
    currentPhases,
    isUpdatingPlan,
    isAnimating,
    highlightedQuestionId,
    // Module celebration
    triggerConfetti,
    celebratingModule,
    dismissModuleCelebration,
    completedModules,
    // Plan completion
    isPlanComplete,
    // Company features (for feature flag checks)
    companyFeatures: data?.getCurrentUser?.company?.features || [],
    planBuilderAgentChatEnabled: (data?.getCurrentUser?.company?.features || []).includes('plan_builder_agent_chat'),
    // Agent chat state
    agentMessages,
    setAgentMessages,
    agentConversationId,
    setAgentConversationId,
    agentLoading,
    setAgentLoading,
    pendingSuggestion,
    setPendingSuggestion,
    acceptSuggestion,
    clearAgentChat,
    injectedAnswer,
    setInjectedAnswer,
    clearInjectedAnswer,
    // Inline AI suggestion (Help me with this answer)
    inlineSuggestion,
    inlineSuggestionLoading,
    inlineSuggestionError,
    fetchInlineSuggestion,
    useInlineSuggestion,
    dismissInlineSuggestion,
    clearInlineSuggestion,
    usedAiSuggestion,
    clearInjectedAnswer
  };

  return (
    <PlanBuilderContext.Provider value={value}>
      {children}
    </PlanBuilderContext.Provider>
  );
};
